"use client";

import { motion } from "motion/react";
import Image from "next/image";
import { BarChart3, Eye } from "lucide-react";

/**
 * The animated "AI detection" card from the homepage hero: creator photo, scan
 * line, blue item boxes, pink logo boxes and the exposure/payout widget.
 * Shared by the hero and the Request Access page.
 */
export function DetectionCard({
  sizes = "(max-width: 768px) 100vw, 50vw",
}: {
  sizes?: string;
}) {
  return (
    <div className="relative bg-white p-2 rounded-[2rem] shadow-2xl shadow-brand-900/15 border border-gray-100">
      <div className="relative rounded-[1.5rem] overflow-hidden bg-gray-900 aspect-[4/5] group">

        <Image
          src="/Revised Photo for Homepage.png"
          alt="Creator content"
          fill
          sizes={sizes}
          className="object-cover opacity-90 group-hover:opacity-75 transition-opacity duration-700"
        />

        {/* Scanning Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-400/20 to-transparent h-32 w-full"
          animate={{ y: ["-100%", "400%"] }}
          transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
        />

        {/* ── BLUE BOXES: whole items ── */}

        {/* Blue: Sweatshirt */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          className="absolute border-2 border-blue-400 bg-blue-400/5 rounded-sm"
          style={{ top: "10%", left: "30%", width: "40%", height: "23%" }}
        >
          <div className="absolute -top-5 left-[-2px] bg-blue-500 text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider rounded-t-sm">
            SWEATSHIRT
          </div>
          <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-blue-400" />
          <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-blue-400" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-blue-400" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-blue-400" />
        </motion.div>

        {/* Blue: Pants */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.4 }}
          className="absolute border-2 border-blue-400 bg-blue-400/5 rounded-sm"
          style={{ top: "35%", left: "30%", width: "48%", height: "52%" }}
        >
          <div className="absolute -top-5 left-[-2px] bg-blue-500 text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider rounded-t-sm">
            PANTS
          </div>
          <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-blue-400" />
          <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-blue-400" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-blue-400" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-blue-400" />
        </motion.div>

        {/* Blue: Yeti Tumbler */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.4 }}
          className="absolute border-2 border-blue-400 bg-blue-400/5 rounded-sm"
          style={{ top: "63%", left: "72%", width: "20%", height: "22%" }}
        >
          <div className="absolute -top-5 left-[-2px] bg-blue-500 text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider rounded-t-sm whitespace-nowrap">
            YETI TUMBLER
          </div>
          <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-blue-400" />
          <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-blue-400" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-blue-400" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-blue-400" />
        </motion.div>

        {/* ── PINK BOXES: logos ── */}

        {/* Pink: Puma logo on sweatshirt chest */}
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.4, duration: 0.35 }}
          className="absolute border-2 border-pink-400 bg-pink-400/10 rounded-sm"
          style={{ top: "15.5%", left: "45%", width: "11%", height: "7%" }}
        >
          <div className="absolute -top-5 left-[-2px] bg-pink-500 text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider rounded-t-sm">
            PUMA LOGO
          </div>
          <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-pink-400" />
          <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-pink-400" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-pink-400" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-pink-400" />
        </motion.div>

        {/* Pink: Puma logo on pants */}
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.65, duration: 0.35 }}
          className="absolute border-2 border-pink-400 bg-pink-400/10 rounded-sm"
          style={{ top: "40.5%", left: "58.5%", width: "11%", height: "6.5%" }}
        >
          <div className="absolute -top-5 left-[-2px] bg-pink-500 text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider rounded-t-sm">
            PUMA LOGO
          </div>
          <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-pink-400" />
          <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-pink-400" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-pink-400" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-pink-400" />
        </motion.div>

        {/* Pink: Yeti logo on tumbler */}
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.9, duration: 0.35 }}
          className="absolute border-2 border-pink-400 bg-pink-400/10 rounded-sm"
          style={{ top: "74%", left: "75%", width: "15%", height: "6%" }}
        >
          <div className="absolute -top-5 left-[-2px] bg-pink-500 text-white text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wider rounded-t-sm">
            YETI LOGO
          </div>
          <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-pink-400" />
          <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-pink-400" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-pink-400" />
          <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-pink-400" />
        </motion.div>

        {/* Floating Stats Widget */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2.2, duration: 0.5 }}
          className="absolute bottom-6 left-6 glass-dark rounded-xl p-4 shadow-2xl w-48"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70 text-xs font-mono uppercase tracking-wider">Exposure Time</span>
            <BarChart3 size={14} className="text-brand-400" />
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-2xl font-display font-bold text-white">12.4</span>
            <span className="text-white/50 text-sm">sec</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-1.5 mt-2 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "65%" }}
              transition={{ delay: 2.5, duration: 1, ease: "easeOut" }}
              className="bg-brand-400 h-full rounded-full"
            />
          </div>
          <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
            <span className="text-white/70 text-xs flex items-center gap-1"><Eye size={10} />Views</span>
            <span className="text-white font-mono font-bold text-sm">150k</span>
          </div>
          <div className="mt-2 flex justify-between items-center">
            <span className="text-white/70 text-xs">Est. Payout</span>
            <span className="text-brand-400 font-mono font-bold text-sm">$450.00</span>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
